import {
  DispatchSource,
  OrderingAlgorithmType,
  Driver,
  Stop,
  Task,
  TaskStatus,
  useGetDriverTasksByDriverIdLazyQuery,
  useReplaceRouteMutation,
} from "@api/graphql/generated/generated-types";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
  DriverAvatar,
  Loading,
} from "@src/common/components";
import { useEffect, useState } from "react";
import { SIZE } from "baseui/modal";
import ChangeDriverRouteView from "@src/common/components/ChangeDriverRouteView/ChangeDriverRouteView";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import CourieButton from "@src/common/components/Button";
import StopDateListTimeline from "@src/common/components/ChangeDriverRouteView/components/StopDateListTimeline";
import { useAuth } from "@src/common/hooks/useAuth";
import moment from "moment";
import * as Sentry from "@sentry/react";

type Props = {
  isModalOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  driver: Driver;
  stopDates: string[];
};

function BulkChangeDriverChangeRoutingSlide({
  isModalOpen,
  onClose,
  onSuccess,
  driver,
  stopDates,
}: Props) {
  const sortedStopDates = stopDates.sort();

  const { courierId, courieUser, employeeId } = useAuth();
  const { showToast } = useCourieStore();
  const [routeDetails, setRouteDetails] =
    useState<Stop[] | undefined>(undefined);
  const [isValidOrder, setIsValidOrder] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderingAlgorithmType, setSelectedOrderingAlgorithmType] =
    useState<OrderingAlgorithmType | undefined>(OrderingAlgorithmType.Manual);
  const [autoRoutedStopIds, setAutoRoutedStopIds] =
    useState<undefined | string[]>(undefined);

  const isLastStopDate =
    !sortedStopDates ||
    sortedStopDates.length === 0 ||
    currentDateIndex === sortedStopDates.length - 1;

  const currentStopDate =
    sortedStopDates && sortedStopDates.length > 0
      ? sortedStopDates[currentDateIndex]
      : moment().format("YYYY-MM-DD");

  const [
    getDriverTasksByDriverId,
    {
      data: GetDriverTasksByDriverIdData,
      loading: GetDriverTasksByDriverIdLoading,
      error: GetDriverTasksByDriverIdError,
      refetch: GetDriverTasksByDriverIdRefetch,
    },
  ] = useGetDriverTasksByDriverIdLazyQuery({
    fetchPolicy: "network-only",
  });

  const [
    replaceRouteMutation,
    {
      data: ReplaceRouteMutationData,
      loading: ReplaceRouteMutationLoading,
      error: ReplaceRouteMutationError,
    },
  ] = useReplaceRouteMutation();

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    const driverId = driver?.id;
    if (!driverId || !isModalOpen || !currentStopDate || !courierId) {
      return;
    }
    getDriverTasksByDriverId({
      variables: {
        courierId: courierId,
        driverId: driverId,
        date: currentStopDate,
        statusFilter: [TaskStatus.Created, TaskStatus.Started],
      },
    }).catch((error) => {
      showErrorToast(error, showToast);
    });
  }, [isModalOpen, currentStopDate, driver]);

  useEffect(() => {
    if (GetDriverTasksByDriverIdLoading) {
      return;
    }
    if (GetDriverTasksByDriverIdData) {
      updateRouteDetails();
    }
  }, [GetDriverTasksByDriverIdData, GetDriverTasksByDriverIdLoading]);

  const onSubmit = () => {
    if (!driver?.id || !routeDetails || !currentStopDate || !courierId) {
      // Capture detailed information about why API call was blocked
      Sentry.captureMessage(
        "BulkChangeDriver: API call blocked - missing required data",
        {
          level: "warning",
          tags: {
            component: "BulkChangeDriverChangeRoutingSlide",
            action: "onSubmit",
          },
          contexts: {
            missingData: {
              hasDriverId: !!driver?.id,
              hasRouteDetails: !!routeDetails,
              hasCurrentStopDate: !!currentStopDate,
              hasCourierId: !!courierId,
              routeDetailsLength: routeDetails?.length || 0,
            },
            driverInfo: {
              driverId: driver?.id || "missing",
              driverFirstName: driver?.firstName || "missing",
            },
            dateInfo: {
              currentStopDate: currentStopDate || "missing",
              currentDateIndex,
              totalStopDates: stopDates.length,
              isLastStopDate,
            },
            algorithmInfo: {
              selectedOrderingAlgorithmType:
                selectedOrderingAlgorithmType || "missing",
              autoRoutedStopIdsCount: autoRoutedStopIds?.length || 0,
            },
            userInfo: {
              courierId: courierId || "missing",
              employeeId: employeeId || "missing",
              courieUserId: courieUser?.id || "missing",
            },
          },
        }
      );
      return;
    }

    const stopIds = routeDetails.map((stop) => stop.id);

    replaceRouteMutation({
      variables: {
        courierId: courierId,
        driverId: driver.id,
        newStopIds: stopIds,
        shipmentDate: currentStopDate,
        replacedBy: employeeId ? employeeId : courieUser!.id,
        orderingAlgorithmType: selectedOrderingAlgorithmType,
        autoRoutedStopIds: autoRoutedStopIds ? autoRoutedStopIds : [],
        dispatchSource: DispatchSource.Dispatcher,
      },
    })
      .then(() => {
        if (isLastStopDate) {
          onSuccess && onSuccess();
        } else {
          setCurrentDateIndex(currentDateIndex + 1);
        }
      })
      .catch((error) => {
        // Capture mutation errors with full context
        Sentry.captureException(error, {
          tags: {
            component: "BulkChangeDriverChangeRoutingSlide",
            action: "replaceRouteMutation",
          },
          contexts: {
            mutationVariables: {
              courierId,
              driverId: driver.id,
              stopIdsCount: stopIds.length,
              shipmentDate: currentStopDate,
              orderingAlgorithmType: selectedOrderingAlgorithmType,
              autoRoutedStopIdsCount: autoRoutedStopIds?.length || 0,
            },
            routeState: {
              currentDateIndex,
              isLastStopDate,
              totalStopDates: stopDates.length,
            },
          },
        });
        showErrorToast(error, showToast);
      });
  };

  useEffect(() => {
    if (ReplaceRouteMutationError) {
      showErrorToast(ReplaceRouteMutationError, showToast);
    }
  }, [ReplaceRouteMutationError]);

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handleManualClick = () => {
    setSelectedOrderingAlgorithmType(OrderingAlgorithmType.Manual);
    if (GetDriverTasksByDriverIdData) {
      updateRouteDetails();
    }
  };

  const updateRouteDetails = () => {
    const tasks =
      (GetDriverTasksByDriverIdData?.driverById?.tasks as Task[]) || [];
    const sortedTasks = tasks.sort((a, b) => {
      return a.sequenceNumber - b.sequenceNumber;
    });
    const stops = sortedTasks.map((task) => task.stop);
    setRouteDetails(stops || []);
    const stopIds = stops.map((stop) => stop.id);
    setAutoRoutedStopIds(stopIds);
  };

  return (
    <BaseModal
      isOpen={isModalOpen}
      closeable={true}
      onClose={handleCloseModal}
      size={SIZE.auto}
      overrides={{
        Dialog: {
          style: {
            overflow: "hidden",
          },
        },
      }}
      autoFocus={false}
    >
      <BaseModalHeader>
        {" "}
        <div className="flex justify-between">
          <div className="flex gap-2">
            <DriverAvatar
              driverPhoto={driver?.photoUrl}
              driverName={driver?.firstName}
              avatarDisplay={driver?.avatarDisplay || undefined}
              size={25}
            />
            Confirm Route ({driver?.firstName})
          </div>
          <div className="">
            <StopDateListTimeline
              listStopDates={sortedStopDates}
              selectedStopDate={
                currentStopDate || moment().format("YYYY-MM-DD")
              }
              shipmentDate={currentStopDate}
            />
          </div>
          <span className="mr-3"></span>
        </div>{" "}
      </BaseModalHeader>
      <BaseModalBody
        style={{ minWidth: 1200, height: "70vh", overflow: "scroll" }}
      >
        <ChangeDriverRouteView
          loading={GetDriverTasksByDriverIdLoading}
          routeDetails={routeDetails}
          setRouteDetails={setRouteDetails}
          setIsValidOrder={setIsValidOrder}
          isValidOrder={isValidOrder}
          selectedShipment={undefined}
          driver={driver}
          setErrorMessage={setErrorMessage}
          selectedStopDate={currentStopDate || moment().format("YYYY-MM-DD")}
          onClickManual={() => {
            handleManualClick();
          }}
          setLoading={setIsLoading}
          onAlgorithmTypeChange={setSelectedOrderingAlgorithmType}
          algorithmType={selectedOrderingAlgorithmType}
          setAutoRoutedStopIds={setAutoRoutedStopIds}
        />
      </BaseModalBody>
      <BaseModalFooter className="flex justify-between">
        {sortedStopDates && sortedStopDates.length > 1 ? (
          <CourieButton
            color={"secondary"}
            onClick={() => {
              const prevStopDateIndex = currentDateIndex - 1;
              if (prevStopDateIndex >= 0) {
                setCurrentDateIndex(prevStopDateIndex);
              }
            }}
          >
            Back
          </CourieButton>
        ) : (
          <CourieButton color={"secondary"} onClick={handleCloseModal}>
            Cancel
          </CourieButton>
        )}
        <Loading loading={ReplaceRouteMutationLoading} className="flex">
          {errorMessage && (
            <span className="text-red-600 my-auto pr-5">{errorMessage}</span>
          )}
          <CourieButton
            isProcessing={ReplaceRouteMutationLoading || isLoading}
            onClick={onSubmit}
            disabled={!isValidOrder || isLoading || ReplaceRouteMutationLoading}
          >
            {isLastStopDate ? `Save` : "Save & Continue"}
          </CourieButton>
        </Loading>
      </BaseModalFooter>
    </BaseModal>
  );
}

export default BulkChangeDriverChangeRoutingSlide;
